import {
	BiometricAuth,
	BiometryError,
	BiometryType,
} from '@aparajita/capacitor-biometric-auth';
import { Capacitor } from '@capacitor/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { useCallback, useEffect, useState } from 'react';

/**
 * Biometric (Face ID / Touch ID / fingerprint) login support.
 *
 * Behavior on native iOS/Android Capacitor shells:
 *   1. After a successful credential login, call `enable(refreshToken, email)`
 *      to securely persist the user's session in the OS keychain. The user
 *      authorizes via Face ID / Touch ID on every read.
 *   2. On subsequent app launches, call `tryAutoLogin()` early in the auth
 *      bootstrap. It triggers the biometric prompt; on success it returns
 *      the stored refresh token so the storefront can hit the regular OAuth
 *      refresh endpoint and skip the email/password screen entirely.
 *   3. `disable()` clears the keychain entry — wire it into Logout.
 *
 * On web (non-native): every method returns `{ available: false }` style
 * results, and `tryAutoLogin()` resolves to `null` immediately. Components
 * can render the regular login form unconditionally; the biometric "fast
 * path" simply never fires.
 *
 * Why two plugins:
 *   - `@aparajita/capacitor-biometric-auth` only handles the prompt — it
 *     doesn't store credentials.
 *   - `capacitor-secure-storage-plugin` persists arbitrary string values to
 *     the iOS Keychain / Android EncryptedSharedPreferences, but doesn't
 *     gate them on biometrics by itself.
 *   We compose them: prompt for biometric → on success, read/write the
 *   keychain entry. The keychain key is namespaced by `appId` so that
 *   tenant-branded apps (HF Foods, Auvis, etc.) keep separate sessions.
 */

const KEYCHAIN_KEY_TOKEN = 'biometric.refreshToken';
const KEYCHAIN_KEY_EMAIL = 'biometric.email';
const KEYCHAIN_KEY_ENABLED = 'biometric.enabled';

export interface BiometricSession {
	email: string;
	refreshToken: string;
}

export interface BiometricCapability {
	/** True only when running inside a native Capacitor shell. */
	isNative: boolean;
	/** True when the device supports biometrics AND has at least one enrolled. */
	isAvailable: boolean;
	/** Friendly identifier for the biometric type ("Face ID", "Touch ID", etc.). */
	biometryName: string;
	biometryType: BiometryType;
}

/**
 * Detects platform + biometric availability without prompting the user.
 * Safe to call on every render; the underlying plugin call is idempotent.
 */
export function useBiometricCapability(): BiometricCapability {
	const [capability, setCapability] = useState<BiometricCapability>(() => ({
		isNative: Capacitor.isNativePlatform(),
		isAvailable: false,
		biometryName: 'Biometric',
		biometryType: BiometryType.none,
	}));

	useEffect(() => {
		if (!Capacitor.isNativePlatform()) return;

		void (async () => {
			try {
				const info = await BiometricAuth.checkBiometry();
				setCapability({
					isNative: true,
					isAvailable: info.isAvailable,
					biometryName: friendlyName(info.biometryType),
					biometryType: info.biometryType,
				});
			} catch (e) {
				// Plugin not registered or device error — treat as unavailable.
				console.debug('[biometric] checkBiometry failed', e);
			}
		})();
	}, []);

	return capability;
}

interface BiometricLoginActions {
	/**
	 * Persist the user's session for biometric unlock. Call this AFTER a
	 * successful credential login. The promise resolves true on success,
	 * false if the user declined the biometric prompt or the device has
	 * no enrolled biometrics.
	 */
	enable: (session: BiometricSession) => Promise<boolean>;

	/**
	 * Drop the stored session. Call from Logout AND when the OAuth refresh
	 * endpoint rejects the stored token (revoked, expired beyond limits,
	 * etc.) so the user falls back to credential login cleanly.
	 */
	disable: () => Promise<void>;

	/**
	 * Triggers the biometric prompt and, on success, returns the persisted
	 * session. Returns null when biometrics aren't enabled, the device
	 * doesn't support them, or the user cancels the prompt. Errors are
	 * swallowed so callers can fall through to the regular login screen.
	 */
	tryAutoLogin: (reason?: string) => Promise<BiometricSession | null>;

	/** True when a biometric session has been persisted on this device. */
	hasStoredSession: boolean;
}

export function useBiometricLogin(): BiometricLoginActions &
	BiometricCapability {
	const capability = useBiometricCapability();
	const [hasStoredSession, setHasStoredSession] = useState(false);

	useEffect(() => {
		if (!capability.isNative) return;
		void (async () => {
			try {
				const enabled = await SecureStoragePlugin.get({
					key: KEYCHAIN_KEY_ENABLED,
				});
				setHasStoredSession(enabled.value === '1');
			} catch {
				// Key not present — that's fine, just means no stored session.
				setHasStoredSession(false);
			}
		})();
	}, [capability.isNative]);

	const enable = useCallback(
		async ({ email, refreshToken }: BiometricSession): Promise<boolean> => {
			if (!capability.isAvailable) return false;
			try {
				await BiometricAuth.authenticate({
					reason: 'Enable quick sign-in with biometrics',
					cancelTitle: 'Cancel',
					allowDeviceCredential: false,
					iosFallbackTitle: '',
					androidTitle: 'Confirm to enable biometric sign-in',
					androidSubtitle: '',
					androidConfirmationRequired: false,
				});
				await SecureStoragePlugin.set({
					key: KEYCHAIN_KEY_TOKEN,
					value: refreshToken,
				});
				await SecureStoragePlugin.set({
					key: KEYCHAIN_KEY_EMAIL,
					value: email,
				});
				await SecureStoragePlugin.set({
					key: KEYCHAIN_KEY_ENABLED,
					value: '1',
				});
				setHasStoredSession(true);
				return true;
			} catch (e) {
				if (e instanceof BiometryError) {
					console.debug('[biometric] enable rejected', e.code);
				} else {
					console.debug('[biometric] enable failed', e);
				}
				return false;
			}
		},
		[capability.isAvailable],
	);

	const disable = useCallback(async (): Promise<void> => {
		if (!capability.isNative) return;
		try {
			await SecureStoragePlugin.remove({ key: KEYCHAIN_KEY_TOKEN });
			await SecureStoragePlugin.remove({ key: KEYCHAIN_KEY_EMAIL });
			await SecureStoragePlugin.remove({ key: KEYCHAIN_KEY_ENABLED });
		} catch (e) {
			// Plugin error or keys missing — either way, we're "disabled".
			console.debug('[biometric] disable cleanup partial', e);
		}
		setHasStoredSession(false);
	}, [capability.isNative]);

	const tryAutoLogin = useCallback(
		async (
			reason = 'Sign in to your account',
		): Promise<BiometricSession | null> => {
			if (!capability.isNative || !capability.isAvailable) return null;

			let enabled = '0';
			try {
				const r = await SecureStoragePlugin.get({ key: KEYCHAIN_KEY_ENABLED });
				enabled = r.value;
			} catch {
				return null;
			}
			if (enabled !== '1') return null;

			try {
				await BiometricAuth.authenticate({
					reason,
					cancelTitle: 'Use email instead',
					allowDeviceCredential: false,
					iosFallbackTitle: '',
					androidTitle: reason,
					androidSubtitle: '',
					androidConfirmationRequired: false,
				});

				const [tokenR, emailR] = await Promise.all([
					SecureStoragePlugin.get({ key: KEYCHAIN_KEY_TOKEN }),
					SecureStoragePlugin.get({ key: KEYCHAIN_KEY_EMAIL }),
				]);

				if (tokenR.value === '' || emailR.value === '') {
					await disable();
					return null;
				}
				return { email: emailR.value, refreshToken: tokenR.value };
			} catch (e) {
				// User cancelled, biometry locked out, or plugin error — let
				// the caller fall through to credential login.
				console.debug('[biometric] auto-login cancelled or failed', e);
				return null;
			}
		},
		[capability.isAvailable, capability.isNative, disable],
	);

	return {
		...capability,
		hasStoredSession,
		enable,
		disable,
		tryAutoLogin,
	};
}

function friendlyName(type: BiometryType): string {
	switch (type) {
		case BiometryType.faceId:
			return 'Face ID';
		case BiometryType.touchId:
			return 'Touch ID';
		case BiometryType.fingerprintAuthentication:
			return 'Fingerprint';
		case BiometryType.faceAuthentication:
			return 'Face Authentication';
		case BiometryType.irisAuthentication:
			return 'Iris';
		default:
			return 'Biometric';
	}
}
